import { useBackgroundTask } from 'components/BackgroundTaskModal/hooks';
import { difference } from 'lodash';
import {
  MutableRefObject,
  useCallback,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { Campaign, Features } from 'types';
import { useProxyParameters } from 'utils/api/ApiHooks';
import { CAMPAIGN_STATUSES } from 'utils/models/Campaign';
import { useFeatures } from 'utils/util/features';
import { useInterval } from 'utils/util/hooks';
import { EmptyFragment, isObjectNonTrivial } from 'utils/util/util';
import { OnInputChange } from 'views/Widgets/Forms/ValidatedForm';
import { INPUT_TYPES } from 'views/Widgets/Inputs/ValidatedInputTypes';
import {
  FAKE_IMPORT_ACTIVITIES_FROM,
  GENERIC_AUXILIARY_INPUT,
  getAiRequestType,
  getAiSuggestions,
  suggestionsToObject,
  TICKETING_SYSTEMS_INPUT,
  TICKETING_SYSTEMS_LOADING_INPUT,
  useTicketingInformation,
} from '../common';
import {
  aiAssistedInputWrapper,
  aiAssistedOutputWrapper,
} from '../Form/ValidatedInputAIWrapper';
import BannerWrapper from './BannerWrapper';
import { ENHANCE_DRAFT_DESCRIPTORS, FIRST_DRAFT_DESCRIPTORS } from './consts';
import { excludeEventsOnExistingNonTrivialProperties } from './utils';

export const useAIAssistedQuestionnaire = ({
  campaign,
  contextPersonId,
  suggestionType,
  scopedPropertyNames,
  featureToggleID,
  enabledForCampaign,
}: {
  campaign: Campaign;
  suggestionType: string;
  contextPersonId: number;
  scopedPropertyNames: string[];
  featureToggleID: keyof Features['ai_assistant'];
  enabledForCampaign: (campaign: Campaign) => boolean;
}) => {
  const [onInputChangeRef, setOnInputChangeRef] =
    useState<() => MutableRefObject<unknown>>();
  const [internalObjectRef, setInternalObjectRef] =
    useState<MutableRefObject<unknown>>();

  const isEnabledForCampaign = enabledForCampaign(campaign);

  const handleReady = useCallback(
    ({
      onInputChangeRef,
      objectRef,
    }: {
      onInputChangeRef: MutableRefObject<OnInputChange | undefined>;
      objectRef: MutableRefObject<unknown>;
    }) => {
      setOnInputChangeRef(() => onInputChangeRef);
      setInternalObjectRef(objectRef);
    },
    []
  );

  const [suggestions, setSuggestions] = useState(undefined);
  const aiAssistedCustomProperties = useMemo(() => {
    return {
      suggestions,
      inputChangeRef: onInputChangeRef,
    };
  }, [suggestions, onInputChangeRef]);

  const handleSuggestionsCallback = useCallback((suggestions) => {
    setSuggestions(suggestions);
  }, []);

  const { formatMessage } = useIntl();
  const { isGroupAndOptionEnabled } = useFeatures();

  const proxyParameters = useProxyParameters();

  const {
    ticketingSystems,
    loading: loadingTicketingSystems,
    fetch: fetchTicketingSystems,
  } = useTicketingInformation({
    campaign,
    contextPersonId,
    lazy: true,
  });

  const [backgroundTaskKey, setBackgroundTaskKey] = useState<
    string | undefined
  >(undefined);
  const onDelayedCallbackRef = useRef();
  const onDelayedHandleCloseRef = useRef();
  const onDelayedHandleErrorRef = useRef();
  const [isPartiallyFilled, setIsPartiallyFilled] = useState<
    boolean | undefined
  >(undefined);

  const checkIfPartiallyFilled = useCallback(() => {
    setIsPartiallyFilled(
      // For the purpose of determining if the form is partially filled,
      // we consider that a form is partially filled if it has at least one
      // non-trivial property filled.
      // @ts-expect-error
      isObjectNonTrivial(internalObjectRef?.current, [
        // discard_id is not considered, since it's a property set when
        // the user discard the draft
        'discard_id',
        ...difference(
          // keys in the form
          Object.keys(internalObjectRef?.current ?? {}),
          // keys in the scoped properties
          scopedPropertyNames
        ),
      ])
    );
  }, [internalObjectRef, scopedPropertyNames]);

  // Workaround to update the banner with the partially filled state information
  useInterval(
    checkIfPartiallyFilled,
    1000,
    isGroupAndOptionEnabled('ai_assistant', featureToggleID) &&
      isEnabledForCampaign
  );

  const handleAiCallback = useCallback(
    (response) => {
      const suggestionsObject = suggestionsToObject(response, suggestionType);
      const listOfEvents = Object.entries(suggestionsObject).map(
        ([key, value]) => ({
          target: {
            name: key,
            // @ts-expect-error
            value: value?.value,
          },
        })
      );

      handleSuggestionsCallback?.(suggestionsObject);
      setTimeout(() => {
        // @ts-expect-error
        onInputChangeRef?.current(
          listOfEvents,
          null,
          excludeEventsOnExistingNonTrivialProperties
        );
      }, 1000);
    },
    [onInputChangeRef, handleSuggestionsCallback, suggestionType]
  );

  const handleAiSubmit = useCallback(
    (object, onCallback, handleClose, handleError) => {
      handleSuggestionsCallback?.(undefined);
      getAiSuggestions(
        {
          ...proxyParameters,
          suggestion_type: suggestionType,
          campaign_id: campaign?.id,
          context_person_id: contextPersonId,
          request_type: getAiRequestType(
            // @ts-expect-error
            campaign?.status === CAMPAIGN_STATUSES.DEMO,
            isGroupAndOptionEnabled('ai_assistant', 'ai_canned_demo_responses')
          ),
          other: {
            ...object,
            draft_data: internalObjectRef?.current,
            omit_data_already_answered: true,
          },
        },
        (response, error) => {
          if (!!response && !error) {
            setBackgroundTaskKey(response?.background_task_key);
            onDelayedCallbackRef.current = onCallback;
            onDelayedHandleCloseRef.current = handleClose;
            onDelayedHandleErrorRef.current = handleError;
          } else if (error) {
            handleError(
              formatMessage({
                id: 'app.views.ai.assisted_questionnaire.use_ai_assisted_questionnaire.api_error',
                defaultMessage:
                  'There was a problem requesting data from the backend, please try again',
              })
            );
          }
        }
      );
    },
    [
      suggestionType,
      handleSuggestionsCallback,
      proxyParameters,
      campaign?.id,
      // @ts-expect-error
      campaign?.status,
      contextPersonId,
      internalObjectRef,
      isGroupAndOptionEnabled,
      formatMessage,
    ]
  );

  const handleOnTaskComplete = useCallback((data) => {
    setBackgroundTaskKey(undefined);
    if (onDelayedCallbackRef.current) {
      // @ts-expect-error
      onDelayedCallbackRef.current(data);
      onDelayedCallbackRef.current = undefined;
    }
    if (onDelayedHandleCloseRef.current) {
      // @ts-expect-error
      onDelayedHandleCloseRef.current();
      onDelayedHandleCloseRef.current = undefined;
    }
    if (onDelayedHandleErrorRef.current) {
      onDelayedHandleErrorRef.current = undefined;
    }
  }, []);

  const handleOnTaskError = useCallback((data) => {
    setBackgroundTaskKey(undefined);
    if (onDelayedHandleErrorRef.current) {
      // @ts-expect-error
      onDelayedHandleErrorRef.current(data);
      onDelayedHandleErrorRef.current = undefined;
    }
    if (onDelayedCallbackRef.current) {
      onDelayedCallbackRef.current = undefined;
    }
    if (onDelayedHandleCloseRef.current) {
      onDelayedHandleCloseRef.current = undefined;
    }
  }, []);

  useBackgroundTask({
    disabled: !backgroundTaskKey,
    taskDescriptor: {
      // @ts-expect-error
      task_key: backgroundTaskKey,
      status: 'submitted',
    },
    onTaskCompleted: handleOnTaskComplete,
    onError: handleOnTaskError,
  });

  const wrapperComponent = useCallback(
    ({ children }) => {
      return isPartiallyFilled !== undefined ? (
        <BannerWrapper isPartiallyFilled={isPartiallyFilled}>
          {children}
        </BannerWrapper>
      ) : null;
    },
    [isPartiallyFilled]
  );

  const bannerInput = useMemo(() => {
    return isGroupAndOptionEnabled('ai_assistant', featureToggleID) &&
      isEnabledForCampaign
      ? {
          type: INPUT_TYPES.CUSTOM_COMPONENT,
          name: 'ai_completion',
          auxiliaryInput: {
            ...GENERIC_AUXILIARY_INPUT(formatMessage, {
              onCallback: handleAiCallback,
              onSubmit: handleAiSubmit,
              onOpen: fetchTicketingSystems,
              additionalInputs: isGroupAndOptionEnabled(
                'ai_assistant',
                'ai_canned_demo_responses'
              )
                ? [FAKE_IMPORT_ACTIVITIES_FROM(formatMessage)]
                : loadingTicketingSystems
                  ? [TICKETING_SYSTEMS_LOADING_INPUT()]
                  : ticketingSystems.length > 0
                    ? [TICKETING_SYSTEMS_INPUT(formatMessage, ticketingSystems)]
                    : [],
              enableAttachments: isGroupAndOptionEnabled(
                'ai_assistant',
                'ai_enable_attachments'
              ),
            }),
            buttonClassName: 'btn btn-primary',
            columnStyle: 'col-12',
            type: 'MODAL',
            ...(isPartiallyFilled
              ? ENHANCE_DRAFT_DESCRIPTORS(formatMessage)
              : FIRST_DRAFT_DESCRIPTORS(formatMessage)),
            WrapperComponent: wrapperComponent,
          },
          component: EmptyFragment,
        }
      : undefined;
  }, [
    formatMessage,
    handleAiCallback,
    handleAiSubmit,
    fetchTicketingSystems,
    isGroupAndOptionEnabled,
    wrapperComponent,
    isPartiallyFilled,
    isEnabledForCampaign,
    featureToggleID,
    loadingTicketingSystems,
    ticketingSystems,
  ]);

  const values = useMemo(
    () => ({
      bannerInput,
      aiAssistedOutputWrapper,
      aiAssistedInputWrapper,
      handleReady,
      aiAssistedCustomProperties,
    }),
    [aiAssistedCustomProperties, bannerInput, handleReady]
  );
  return values;
};
